// PageContainer — the single source of truth for page main-column width and
// inline padding. Per MOBILE-UX-goal-prompt.md acceptance #2: every route
// that renders inside AppShell must use this wrapper so width, max-width and
// inline padding stay consistent across modules.
//
// Token names:
//   mobile:  px-s-4  (16 px)  /  max-w-screen-md (672 px) when screen ≥ 768 px
//   desktop: px-s-6  (24 px)  /  max-w-screen-md  /  lg  variant available
//
// All values come from DESIGN.md spacing scale (s-1 … s-12). No magic numbers.

import type { ReactNode } from "react";

export type PageContainerWidth = "narrow" | "default" | "wide";

export interface PageContainerProps {
  children: ReactNode;
  /** Layout width preset. `default` (672 px) is the canonical inner-page width. */
  width?: PageContainerWidth;
  /** Extra class names — escape hatch for one-off vertical padding (e.g. above a sticky header). */
  className?: string;
  /** Render as a `<main>` element (true) or `<div>` (false). Use the `<main>` element for the canonical page body. */
  as?: "main" | "div";
  /** `id` to anchor deep-links (e.g. /dashboard#today). */
  id?: string;
  /**
   * Vertical padding rhythm preset.
   * - `default`  → py-s-5  (20 px)
   * - `loose`    → py-s-6  (24 px) for hero-heavy pages
   * - `compact`  → py-s-4  (16 px) for dense list views
   */
  vertical?: "default" | "loose" | "compact";
}

const WIDTH_CLASS: Record<PageContainerWidth, string> = {
  narrow: "max-w-[560px]",
  default: "max-w-screen-md",
  wide: "max-w-screen-lg",
};

const VERTICAL_CLASS: Record<NonNullable<PageContainerProps["vertical"]>, string> = {
  default: "py-s-5",
  loose: "py-s-6",
  compact: "py-s-4",
};

/**
 * Wrap every page body in AppShell with this primitive so spacing stays
 * consistent. The base is `mx-auto` so on screens > max-w the content stays
 * centered; on screens ≤ max-w the inline padding governs the gutter.
 */
export function PageContainer({
  children,
  width = "default",
  className = "",
  as = "main",
  id,
  vertical = "default",
}: PageContainerProps): JSX.Element {
  const Comp = as;
  return (
    <Comp
      id={id}
      data-component="PageContainer"
      data-width={width}
      className={[
        "mx-auto w-full",
        WIDTH_CLASS[width],
        "px-s-4 md:px-s-6",
        VERTICAL_CLASS[vertical],
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </Comp>
  );
}
